import { useMemo } from "react";
import {
  OperatorManyUseCaseEnum,
  useOperatorsManyPersonConnectToListingQuery,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";
import type { UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";

/**
 * Listings the purchaser can access that also exist in an organization's directory
 * (e.g. broadcast storefront checkout scoped to the broadcast owner's org).
 */
export function useOrgScopedUserOperators(
  userOperators: UserOperator[] | undefined,
  organizationId: string | undefined,
  enabled = true,
) {
  const userOperatorEntityIds = useMemo(
    () => (userOperators ?? []).map(op => op.entityId),
    [userOperators],
  );

  const shouldScopeToOrg = Boolean(enabled && organizationId);

  const { data, loading } = useOperatorsManyPersonConnectToListingQuery({
    variables: {
      useCase: OperatorManyUseCaseEnum.Internal,
      includeInvitedOrAcceptedStaffCounts: false,
      filter: {
        organizations: [organizationId!],
        _entityId: userOperatorEntityIds,
      },
      limit: 10000,
    },
    skip: !shouldScopeToOrg || userOperatorEntityIds.length === 0,
  });

  const operators = useMemo((): UserOperator[] => {
    if (!shouldScopeToOrg) return [];
    return (data?.operatorMany ?? []).map(op => ({
      entityId: op._entityId,
      name: getMultilingualString(op.name ?? {}),
      organizationIds: userOperators?.find(u => u.entityId === op._entityId)
        ?.organizationIds,
    }));
  }, [shouldScopeToOrg, data, userOperators]);

  return {
    operators,
    loading: shouldScopeToOrg && loading,
  };
}
